import { useCallback, useEffect, useRef, useState } from 'react'
import { readUi, watchUi, writeUi } from './uiPrefs'

/**
 * A piece of state the page keeps: how somebody has arranged it -- the
 * conversation folded away, the board filling the window, the skills rail
 * shut. A setting they made on purpose, and once had taken back from them on
 * every visit.
 *
 * Kept with the account since 2026-10-02 (uiPrefs.ts), so it follows the
 * person to another computer; the browser's copy draws the first frame, and
 * the account's replaces it if it differs.
 *
 * Only a value of the shape that was asked for is taken: a boolean arriving
 * as a string would be true for ever after.
 */
export function useRemembered<T>(key: string, fallback: T): [T, (value: T) => void] {
  const valid = (value: unknown) => typeof value === typeof fallback
  const [value, set] = useState<T>(() => readUi(key, fallback, valid))

  useEffect(
    () =>
      watchUi(key, (kept) => {
        if (typeof kept === typeof fallback) set(kept as T)
      }),
    // `fallback` only says the shape; it does not change what is watched.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key],
  )

  const remember = useCallback(
    (next: T) => {
      set(next)
      writeUi(key, next)
    },
    [key],
  )

  return [value, remember]
}

/**
 * The same, kept per project (Huanrui, 2026-10-13): how a project's page is
 * arranged -- the conversation full window or folded, the skills rail open --
 * belongs to that project. One used only for talking opens as a conversation;
 * one used only as a board opens as a board. A new project starts from the
 * defaults, and what it is given before it has an id is kept once it has one.
 */
export function useProjectRemembered<T>(projectId: string, name: string, fallback: T): [T, (value: T) => void] {
  const key = projectId ? `ww.project.${projectId}.${name}` : ''
  const valid = (value: unknown) => typeof value === typeof fallback
  const [value, set] = useState<T>(() => (key ? readUi(key, fallback, valid) : fallback))
  const lastKey = useRef(key)
  const latest = useRef(value)
  latest.current = value

  useEffect(() => {
    const was = lastKey.current
    lastKey.current = key
    if (was === key) return
    // A new project has just been given its id: what it has now is its own.
    if (!was && key) {
      writeUi(key, latest.current)
      return
    }
    set(key ? readUi(key, fallback, valid) : fallback)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  useEffect(() => {
    if (!key) return
    return watchUi(key, (kept) => {
      if (typeof kept === typeof fallback) set(kept as T)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  const remember = useCallback(
    (next: T) => {
      set(next)
      if (key) writeUi(key, next)
    },
    [key],
  )
  return [value, remember]
}
